/**
 * useRecordAutosave -- debounced autosave for a form that edits ONE record.
 *
 * Used by the Company page and Super Admin Edit Distributor (Bug Intake #418,
 * Jake 2026-10-23: they save as you go, like the store editor, and the
 * separate Save button goes away). The store editor (StoreAddEdit.vue) holds
 * its own copy of the same state machine; this is the part that is not
 * welded to a store, with the SAME safeguards from PR #4707 (Bug Intake #445):
 *
 *   - Safety valve: the debounce runs at most AUTOSAVE_MAX_IDENTICAL_SAVES
 *     saves in a row that leave the record identical, with no input from a
 *     person in between (input/change/keydown/pointerdown/paste/drop on the
 *     document). Past that it stops, says so, and a person's next edit
 *     resumes it. A burst of DIFFERENT saves never counts: only a loop saves
 *     the same thing again and again.
 *   - Trusted input only: a code-dispatched event cannot reopen a stopped
 *     loop (`event.isTrusted`).
 *   - Leaving flushes: `flush()` saves a pending edit right now and is never
 *     metered by the valve, so a halted form still saves on the way out.
 *
 * On top of that, two rules the store editor does not need:
 *   - `validate()` runs before EVERY save. A record that fails is not sent,
 *     and the status says "invalid", never "saved".
 *   - "saved" only ever follows an `{ ok: true }` from the host's `save()`.
 *
 * Options
 *   save()       async () => { ok: true } | { ok: false, error? }. A throw is
 *                an error. The host owns transport and error wording and must
 *                never put field values in `error`.
 *   validate()   optional async () => boolean (falsy blocks the save).
 *   signature()  () => string identifying what would be saved now (the valve
 *                compares consecutive saves). Never put secrets in it.
 *   debounceMs   quiet period before a save (default 1000).
 *
 * Returns
 *   status   ref: "saved" | "dirty" | "saving" | "invalid" | "error"
 *   message  ref: why, for "invalid" / "error"; "" otherwise
 *   halted   ref: the valve stopped autosave
 *   markDirty()  the record changed because of the person; arms the debounce
 *   flush()      async; saves pending work now; resolves true when saved
 *   dispose()    cancels the timer and removes the listeners. The HOST calls it
 *                on unmount, AFTER a final flush() (a pending edit is saved,
 *                never dropped).
 *   onOwnerInput(event)  the document listener (exposed for tests)
 */
import { ref } from "vue";

export const AUTOSAVE_DEBOUNCE_MS = 1000;
export const AUTOSAVE_MAX_IDENTICAL_SAVES = 5;

const OWNER_INPUT_EVENTS = ["input", "change", "keydown", "pointerdown", "paste", "drop"];

export function useRecordAutosave({
  save,
  validate = null,
  signature = () => "",
  debounceMs = AUTOSAVE_DEBOUNCE_MS,
}) {
  const status = ref("saved");
  const message = ref("");
  const halted = ref(false);

  let timer = null;
  let inFlight = null;
  let pending = false;
  let disposed = false;
  let identicalSaves = 0;
  let lastSignature = null;

  function setStatus(next, text = "") {
    status.value = next;
    message.value = text;
  }

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function arm() {
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      run({ metered: true });
    }, debounceMs);
  }

  // After every save attempt (good, refused or invalid): did it leave the
  // record exactly as the previous one did? A run of those with no person
  // typing between is the loop's signature.
  function noteResult() {
    const current = signature();
    identicalSaves = current === lastSignature ? identicalSaves + 1 : 1;
    lastSignature = current;
  }

  function halt() {
    pending = false;
    halted.value = true;
    setStatus(
      "error",
      "Autosave stopped: the page kept saving with no new edits from you. " +
        "Reload the page to see what was saved. Your next edit tries again."
    );
    console.warn(
      "[useRecordAutosave] autosave stopped: " +
        AUTOSAVE_MAX_IDENTICAL_SAVES +
        " identical saves in a row with no input from a person"
    );
  }

  async function run({ metered }) {
    if (disposed) return false;
    if (inFlight) {
      // Not a new save yet: the in-flight one re-arms when it finishes.
      pending = true;
      return inFlight;
    }
    if (metered && identicalSaves >= AUTOSAVE_MAX_IDENTICAL_SAVES) {
      halt();
      return false;
    }
    inFlight = (async () => {
      try {
        if (typeof validate === "function") {
          let valid = false;
          try {
            valid = !!(await validate());
          } catch (err) {
            valid = false;
          }
          if (!valid) {
            setStatus("invalid", "Not saved yet: fix the highlighted fields.");
            return false;
          }
        }
        setStatus("saving");
        let result;
        try {
          result = await save();
        } catch (err) {
          result = { ok: false };
        }
        if (!result || !result.ok) {
          setStatus(
            "error",
            (result && result.error) || "Could not save your changes. Nothing was changed."
          );
          return false;
        }
        halted.value = false;
        setStatus("saved");
        return true;
      } finally {
        inFlight = null;
        if (!disposed) noteResult();
        if (pending && !disposed) {
          pending = false;
          // The record moved on while we were saving: it is not saved yet.
          if (status.value === "saved") setStatus("dirty");
          arm();
        }
      }
    })();
    return inFlight;
  }

  function markDirty() {
    if (disposed) return;
    if (inFlight) {
      pending = true;
      return;
    }
    // A person's edit while stopped or failed is a fresh attempt.
    setStatus("dirty");
    arm();
  }

  async function flush() {
    clearTimer();
    if (inFlight) await inFlight;
    if (status.value === "saved" && !pending) return true;
    pending = false;
    return !!(await run({ metered: false }));
  }

  function onOwnerInput(event) {
    // Only the browser's own events (a person) count: code that dispatches
    // one must not be able to reopen a stopped loop.
    if (!event || !event.isTrusted) return;
    identicalSaves = 0;
    lastSignature = null;
  }

  function onBeforeUnload(event) {
    if (status.value === "saved" || disposed) return;
    event.preventDefault();
    event.returnValue = "";
  }

  if (typeof document !== "undefined") {
    for (const type of OWNER_INPUT_EVENTS) {
      document.addEventListener(type, onOwnerInput, true);
    }
    window.addEventListener("beforeunload", onBeforeUnload);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    clearTimer();
    if (typeof document !== "undefined") {
      for (const type of OWNER_INPUT_EVENTS) {
        document.removeEventListener(type, onOwnerInput, true);
      }
      window.removeEventListener("beforeunload", onBeforeUnload);
    }
  }

  return { status, message, halted, markDirty, flush, dispose, onOwnerInput };
}
